import React, { useState, useEffect } from "react";
import { IoCheckmarkCircle, IoClose, IoWarning } from "react-icons/io5";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import MpesaLogo from "../../assets/img/ecommerce/Mpesa.png";
import WaafiPayLogo from "../../assets/img/ecommerce/WaafiPay.png";
import { subscribeToCourse, subscribeAndPay, validateReferralCode, getUserProfile } from "api";
import AnimatedCheckmark from "components/dataDisplay/AnimatedCheckmark";
import { parsePaymentError, showPaymentError, isRetryableError, startCooldown, isInCooldown, getCooldownRemaining, clearCooldown } from "../../utils/errorHandler";

const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-orange-500 border-gray-200 rounded-full animate-spin"></div>
);

const SubscriptionModal = ({ onClose, parentName, courseId, userId, mentorId, country: initialCountry = "", isRenewal = false }) => {
    const [currency, setCurrency] = useState(
    initialCountry === "Somalia" ? "USD" : "KES");
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [referralCode, setReferralCode] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState(initialCountry);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [referralVisible, setReferralVisible] = useState(false);
  const [showRefPrompt, setShowRefPrompt] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(15);
  const [discountedAmount, setDiscountedAmount] = useState(15);
  const [finalAmount, setFinalAmount] = useState("15.00");
  const [exchangeRates, setExchangeRates] = useState({});
  const [discountApplied, setDiscountApplied] = useState(false);
  const [schoolSponsored, setSchoolSponsored] = useState(false);
  
  // Cooldown state for real-time updates
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isInCooldownState, setIsInCooldownState] = useState(false);

  const currencyOptions = ["KES", "USD", "EUR", "GBP", "JPY", "INR"];

  // Normalize phone number input
  const handlePhoneNumberChange = (e) => {
    let value = e.target.value.replace(/[\s-]/g, "");
    if (country === "Somalia" && !value.startsWith("+252") && value.startsWith("0")) {
      value = `+252${value.slice(1)}`;
    }
    setPhoneNumber(value);
  };

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["userProfile", userId],
    queryFn: async () => {
      const response = await getUserProfile();
      return response;
    },
    onSuccess: (data) => {
      setPhoneNumber(data.user.phone || "");
      if (!initialCountry) {
        const userCountry = data.user.country;
        const valid = ["Kenya", "Somalia"].includes(userCountry) ? userCountry : "Kenya";
        setCountry(valid);
        setCurrency(valid === "Somalia" ? "USD" : "KES");
      }
    },
    onError: () => {
      setErrorMessage("Failed to load profile. Please select your country.");
      setCountry("Kenya");
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      if (exchangeRates[currency]) return;
      try {
        const response = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
        const data = await response.json();
        setExchangeRates({ ...exchangeRates, ...data.rates, USD: 1 });
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    };
    fetchExchangeRates();
  }, [currency, exchangeRates]);

  // Set base and discounted amounts based on subscription type
  useEffect(() => {
    const baseAmount = subscriptionType === "yearly" ? 15 * 12 : 15;
    const discount = subscriptionType === "yearly" ? baseAmount * 0.7 : baseAmount;
    setOriginalAmount(baseAmount);
    setDiscountedAmount(discount);
    setSchoolSponsored(false);
    setDiscountApplied(false);
    setShowRefPrompt(false);
  }, [subscriptionType]);

  // Calculate final amount using exchange rates
  useEffect(() => {
    const exchangeRate = country === "Somalia" ? 1 : exchangeRates[currency] || 1;
    const final = Number(discountedAmount) * exchangeRate;
    setFinalAmount(final.toFixed(2));
  }, [currency, discountedAmount, exchangeRates, country]);

  // Real-time cooldown updates for WaafiPay
  useEffect(() => {
    if (country !== "Somalia") return;

    const interval = setInterval(() => {
      const remaining = getCooldownRemaining(userId);
      const inCooldown = isInCooldown(userId);
      
      setCooldownRemaining(remaining);
      setIsInCooldownState(inCooldown);
    }, 1000);

    return () => clearInterval(interval);
  }, [country, userId]);

  const skipPayment = Number(discountedAmount) === 0;
  const paymentProvider = country === "Somalia" ? "WaafiPay" : "M-Pesa";
  const logo = country === "Somalia" ? WaafiPayLogo : MpesaLogo;

  // Mutation to validate the referral code
  const referralMutation = useMutation({
    mutationFn: async ({ referralCode, discountedAmount, subscriptionType }) => {
      if (!referralCode) throw new Error("No referral code provided");
      const { discountedPrice, schoolSponsored } = await validateReferralCode(
        referralCode,
        discountedAmount,
        subscriptionType
      );
      return { discountedPrice, schoolSponsored };
    },
    onSuccess: ({ discountedPrice, schoolSponsored }) => {
      setDiscountedAmount(schoolSponsored ? 0 : discountedPrice);
      setDiscountApplied(true);
      setSchoolSponsored(schoolSponsored);
      setErrorMessage("");
    },
    onError: () => {
      setErrorMessage("Invalid or expired referral code.");
      setDiscountApplied(false);
      setSchoolSponsored(false);
    },
  });

  const handleApplyReferralCode = () => {
    if (!referralCode.trim()) {
      setErrorMessage("Please enter a referral code.");
      return;
    }
    referralMutation.mutate({ referralCode, discountedAmount, subscriptionType });
  };

  // Mutation for WaafiPay payment (Somalia) or school-sponsored/free subscription
  const waafiPaymentMutation = useMutation({
    mutationFn: async () => {
      if (country === "Somalia" && !skipPayment && !phoneNumber.match(/^\+252[6-7]\d{8}$/)) {
        throw new Error("Please enter a valid Somali phone number (e.g., +2526XXXXXXXX or +2527XXXXXXXX)");
      }
      const response = await subscribeAndPay({
        userId,
        courseId,
        referralCode: referralCode || undefined,
        subscriptionType,
        phoneNumber: country === "Somalia" && !skipPayment ? phoneNumber : undefined,
        country,
      });
      return response;
    },
    onSuccess: () => {
      setSubscriptionSuccess(true);
      setErrorMessage("");
      // Clear cooldown when WaafiPay payment succeeds
      if (country === "Somalia") {
        clearCooldown(userId);
      }
    },
    onError: (error) => {
      // Only apply enhanced error handling for WaafiPay (Somalia)
      if (country === "Somalia") {
        const errorInfo = parsePaymentError(error);
        setErrorMessage(errorInfo.message);
        
        // Start cooldown for retryable WaafiPay errors
        if (errorInfo.requiresCooldown) {
          startCooldown(userId);
        }
        
        // Show detailed error toast for WaafiPay errors
        if (errorInfo.code && errorInfo.code !== 'UNKNOWN_ERROR') {
          showPaymentError(errorInfo, isRetryableError(error) ? handleRetry : null, userId);
        }
      } else {
        // Default error handling for M-Pesa
        setErrorMessage(error.message || "Error processing your subscription. Please try again.");
      }
      
      setIsProcessing(false);
    },
  });

  // Mutation for M-Pesa subscription with reference number (Kenya)
  const mpesaSubscriptionMutation = useMutation({
    mutationFn: async () => {
      if (!skipPayment && !referenceNumber.trim()) {
        throw new Error("Please enter the payment reference number.");
      }
      const response = await subscribeToCourse({
        userId,
        courseId,
        mentorId,
        referenceNumber: skipPayment ? undefined : referenceNumber,
        subscriptionType,
      });
      return response;
    },
    onSuccess: () => {
      setSubscriptionSuccess(true);
      setErrorMessage("");
    },
    onError: (error) => {
      setErrorMessage(error.message || "Error processing your subscription. Please try again.");
      setIsProcessing(false);
    },
  });

  const handleConfirmSubscription = () => {
    if (isProcessing || (!country && !profileLoading)) return;
    if (country === "Kenya" && !skipPayment && !showRefPrompt) {
      setShowRefPrompt(true);
      return;
    }
    if (country === "Kenya" && !skipPayment && !referenceNumber.trim()) {
      setErrorMessage("Please enter the payment reference number.");
      return;
    }
    setIsProcessing(true);
    setErrorMessage("");
    if (country === "Kenya") {
      mpesaSubscriptionMutation.mutate();
    } else {
      waafiPaymentMutation.mutate();
    }
  };

  const handleRetry = () => {
    // Check if user is in cooldown for WaafiPay
    if (country === "Somalia" && isInCooldownState) {
      return; // Don't allow retry if in cooldown
    }
    
    setErrorMessage("");
    setIsProcessing(false);
    setShowRefPrompt(false);
    setReferenceNumber("");
    setPhoneNumber(profile?.user?.phone || "");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-lg w-full max-w-lg p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
        >
          <IoClose size={24} />
        </button>
        <h2 className="text-xl font-semibold text-center text-navy-700 dark:text-white">
          {isRenewal ? "Renew Subscription to" : "Subscribe to"} {parentName}
        </h2>

        {subscriptionSuccess ? (
          <div className="flex flex-col items-center mt-6">
            <AnimatedCheckmark />
            <p className="mt-4 text-center text-gray-700 dark:text-gray-300">
              {isRenewal ? "Renewal successful!" : "Subscription successful!"} Your transaction is under review.
            </p>
            <button
              onClick={onClose}
              className="w-full mt-4 py-3 text-white bg-orange-600 rounded-md hover:bg-orange-900 transition dark:bg-navy-800 dark:hover:bg-navy-600"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {profileLoading ? (
              <div className="flex justify-center items-center mt-6">
                <Spinner />
              </div>
            ) : (
              <>
                {country === "" && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-navy-700 dark:text-white">
                      Select Your Country
                    </h3>
                    <select
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        setCurrency(e.target.value === "Somalia" ? "SOS" : "KES");
                      }}
                      className="w-full p-2 border rounded-md bg-gray-100 dark:bg-navy-700 dark:text-white mt-2"
                    >
                      <option value="">Select a country</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Somalia">Somalia</option>
                    </select>
                  </div>
                )}

                {country && (
                  <>
                    <div className="mt-6 flex justify-center space-x-4">
                      {["monthly", "yearly"].map((type) => (
                        <button
                          key={type}
                          className={`px-4 py-2 rounded-md ${
                            subscriptionType === type
                              ? "bg-orange-500 text-white"
                              : "bg-gray-200 dark:bg-navy-700 dark:text-white"
                          }`}
                          onClick={() => setSubscriptionType(type)}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>

                    <div className="mt-6 flex justify-between items-center">
                      {subscriptionType === "yearly" && (
                        <p className="text-gray-500 line-through">
                          {currency} {originalAmount}
                        </p>
                      )}
                      <motion.div
                        className="text-3xl font-bold text-navy-700 dark:text-white"
                        initial={{ opacity: 0.9 }}
                        animate={{ opacity: 1 }}
                      >
                        {currency} {finalAmount}
                      </motion.div>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        className="py-2 px-3 border rounded-md bg-gray-100 dark:bg-navy-700 dark:text-white"
                      >
                        {currencyOptions.map((curr) => (
                          <option key={curr} value={curr}>
                            {curr}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mt-6">
                      <h3 className="text-lg font-medium text-navy-700 dark:text-white">
                        Payment Details
                      </h3>
                      <div className="mt-4 bg-gray-50 dark:bg-navy-700 p-4 rounded-md flex justify-between items-center">
                        <div>
                          {country === "Kenya" && !skipPayment ? (
                            <>
                              <p>
                                Paybill: <strong>880100</strong>
                              </p>
                              <p>
                                Account: <strong>072370</strong>
                              </p>
                            </>
                          ) : (
                            <div>
                              <p className="text-gray-700 dark:text-gray-300 mb-2">
                                Pay via {paymentProvider}
                              </p>
                              <input
                                type="text"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="Enter Somali phone number (e.g., +2526XXXXXXXX)"
                                className="w-full p-2 border rounded-md dark:bg-navy-900 dark:border-navy-700 dark:text-white"
                              />
                            </div>
                          )}
                        </div>
                        <img src={logo} alt={paymentProvider} className="h-8" />
                      </div>
                      {errorMessage && (
                        <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                          <div className="flex items-start gap-2">
                            <IoWarning size={20} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                                {errorMessage}
                              </p>
                              {/* Enhanced error tips only for WaafiPay (Somalia) */}
                              {country === "Somalia" && (
                                <>
                                  {errorMessage.includes("Insufficient funds") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Add money to your WaafiPay account and try again
                                    </p>
                                  )}
                                  {errorMessage.includes("Invalid phone number") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Verify your phone number is registered with WaafiPay
                                    </p>
                                  )}
                                  {errorMessage.includes("Network") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Check your internet connection and try again
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {isProcessing && (
                        <p className="mt-2 text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <Spinner />
                          Processing your subscription...
                        </p>
                      )}
                    </div>

                    <div className="mt-6">
                      <button
                        className="text-orange-500 hover:underline"
                        onClick={() => setReferralVisible(!referralVisible)}
                      >
                        {referralVisible ? "Hide Referral Code" : "Have a referral code?"}
                      </button>
                      {referralVisible && (
                        <div className="mt-2 space-y-2">
                          <input
                            type="text"
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            placeholder="Enter referral code"
                            className="w-full p-2 border rounded-md dark:bg-navy-900 dark:border-navy-700 dark:text-white"
                          />
                          <button
                            onClick={handleApplyReferralCode}
                            className="w-full py-2 bg-orange-500 text-white rounded-md"
                            disabled={referralMutation.isLoading}
                          >
                            {referralMutation.isLoading ? <Spinner /> : "Apply Code"}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-6">
                      {skipPayment ? (
                        <button
                          onClick={handleConfirmSubscription}
                          disabled={isProcessing || !country}
                          className="w-full py-3 text-white bg-orange-500 rounded-md"
                        >
                          {isProcessing ? "Processing..." : "Subscribe"}
                        </button>
                      ) : country === "Kenya" ? (
                        <>
                          {showRefPrompt ? (
                            <>
                              <input
                                type="text"
                                value={referenceNumber}
                                onChange={(e) => setReferenceNumber(e.target.value)}
                                placeholder="Enter Payment Reference Number"
                                className="w-full p-2 border rounded-md dark:bg-navy-900 dark:border-navy-700 dark:text-white"
                              />
                              <button
                                onClick={handleConfirmSubscription}
                                disabled={isProcessing}
                                className="w-full py-3 mt-4 text-white bg-orange-500 rounded-md"
                              >
                                {isProcessing ? "Processing..." : "Confirm Payment"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setShowRefPrompt(true)}
                              className="w-full py-3 text-white bg-orange-500 rounded-md"
                            >
                              Subscribe
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          onClick={handleConfirmSubscription}
                          disabled={isProcessing || !phoneNumber || !country || (country === "Somalia" && isInCooldownState)}
                          className={`w-full py-3 text-white rounded-md transition-colors ${
                            isProcessing || !phoneNumber || !country || (country === "Somalia" && isInCooldownState)
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-orange-500 hover:bg-orange-600"
                          }`}
                        >
                          {isProcessing ? "Processing..." : 
                           country === "Somalia" && isInCooldownState ? `Wait ${cooldownRemaining}s` : 
                           "Pay Now"}
                        </button>
                      )}
                    </div>

                    {errorMessage && !isProcessing && (
                      <button
                        onClick={handleRetry}
                        disabled={country === "Somalia" && isInCooldownState}
                        className={`w-full py-3 mt-4 rounded-md transition-colors ${
                          country === "Somalia" && isInCooldownState
                            ? "text-gray-400 bg-gray-100 dark:bg-navy-900 cursor-not-allowed"
                            : "text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-navy-800 hover:bg-gray-300 dark:hover:bg-navy-700"
                        }`}
                      >
                        {country === "Somalia" && isInCooldownState 
                          ? `Wait ${cooldownRemaining}s`
                          : country === "Somalia" && errorMessage.includes("Insufficient funds") 
                            ? "Add Funds & Retry" 
                            : "Retry Payment"
                        }
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="w-full py-3 mt-1 text-gray-500 dark:text-gray-300"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionModal;